/**
 * Watchlisted emitters and other live threats, on the mission map.
 *
 * The Followers tab is the ranked list; this is the same claim as a pin.
 * Without it a confirmed, watchlisted follower exists only as a row — the
 * route and our own devices draw, and the place they were heard does not.
 */
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Marker } from 'react-map-gl';
import { RichTooltip } from '../../../components/common';
import {
  MissionCotraveler,
  MissionRouteEvent,
  listCotravelers,
} from '../../../services/missionApi';
import { useMapIconLabels, useMapLayerVisible } from '../../map/mapLayerVisibility';
import { missionThreatPins, MissionThreatPin } from './threatPins';
import { pinLabelSide, pinLabelSx } from './pinLabels';

export interface MissionThreatLayerProps {
  missionId: number;
  events?: MissionRouteEvent[];
  /** Bumped when the monitor tick lands, so pins follow the run. */
  refreshKey?: number;
  onSelect?: (latitude: number, longitude: number) => void;
  interactive?: boolean;
  /** Event ids a hovered option would clear — those pins go quiet. */
  clearedEventIds?: number[];
  /** Event ids a hovered option still leaves — those stay hot. */
  exposedEventIds?: number[];
}

const MissionThreatLayer: React.FC<MissionThreatLayerProps> = ({
  missionId,
  events = [],
  refreshKey,
  onSelect,
  interactive = true,
  clearedEventIds,
  exposedEventIds,
}) => {
  const [followers, setFollowers] = useState<MissionCotraveler[]>([]);
  const visible = useMapLayerVisible('threats');
  const showIconLabels = useMapIconLabels();

  useEffect(() => {
    let cancelled = false;
    listCotravelers(missionId)
      .then((rows) => { if (!cancelled) setFollowers(rows); })
      .catch(() => { if (!cancelled) setFollowers([]); });
    return () => { cancelled = true; };
  }, [missionId, refreshKey]);

  const pins = missionThreatPins(followers, events);
  if (!visible) return null;
  const cleared = new Set(clearedEventIds || []);
  const exposed = new Set(exposedEventIds || []);
  const previewing = cleared.size > 0 || exposed.size > 0;
  const clearedKeys = new Set<string>();
  const exposedKeys = new Set<string>();
  if (previewing) {
    for (const event of events) {
      const key = (event.threat_key || '').toLowerCase();
      if (!key) continue;
      if (cleared.has(event.id)) clearedKeys.add(key);
      if (exposed.has(event.id)) exposedKeys.add(key);
    }
  }

  return (
    <>
      {pins.map((pin) => {
        let dim = false;
        if (previewing) {
          if (pin.id.startsWith('event:')) {
            const eventId = Number(pin.id.slice(6));
            dim = Number.isFinite(eventId) && cleared.has(eventId) && !exposed.has(eventId);
          } else {
            const key = pin.id.toLowerCase();
            dim = clearedKeys.has(key) && !exposedKeys.has(key);
          }
        }
        const labelSide = showIconLabels
          ? pinLabelSide(
            pins.map((p) => ({ id: p.id, latitude: p.latitude, longitude: p.longitude })),
            pin.id,
            'right',
          )
          : 'hidden';
        return (
          <ThreatMarker
            key={pin.id}
            pin={pin}
            interactive={interactive}
            dimmed={dim}
            labelSide={labelSide}
            onSelect={onSelect}
          />
        );
      })}
    </>
  );
};

const ThreatMarker: React.FC<{
  pin: MissionThreatPin;
  interactive: boolean;
  dimmed?: boolean;
  labelSide?: ReturnType<typeof pinLabelSide>;
  onSelect?: (latitude: number, longitude: number) => void;
}> = ({ pin, interactive, dimmed, labelSide = 'right', onSelect }) => {
  const clickable = !!onSelect && interactive;
  return (
    <Marker
      longitude={pin.longitude}
      latitude={pin.latitude}
      anchor="center"
      style={{ pointerEvents: interactive ? 'auto' : 'none' }}
    >
      <RichTooltip
        placement="top"
        eyebrow={pin.kind === 'watchlist' ? 'watchlist' : pin.kind}
        eyebrowTone={pin.color}
        body={<Box component="strong" sx={{ fontWeight: 700 }}>{pin.label}</Box>}
        hint={pin.subtitle}
      >
        <Box
          role={onSelect ? 'button' : undefined}
          tabIndex={clickable ? 0 : -1}
          aria-label={`${pin.label}: ${pin.subtitle}`}
          onClick={onSelect ? () => onSelect(pin.latitude, pin.longitude) : undefined}
          onKeyDown={(e) => {
            if (!onSelect) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelect(pin.latitude, pin.longitude);
            }
          }}
          sx={{
            position: 'relative',
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: clickable ? 'pointer' : 'default',
            color: pin.color,
            opacity: dimmed ? 0.28 : 1,
            filter: dimmed ? 'grayscale(0.8)' : 'none',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              width: 22,
              height: 22,
              borderRadius: '50%',
              border: `1.5px solid ${pin.color}`,
              bgcolor: `${pin.color}2e`,
              filter: `drop-shadow(0 0 4px ${pin.color})`,
              '@keyframes missionThreatRing': {
                '0%': { transform: 'scale(0.7)', opacity: 0.7 },
                '75%': { transform: 'scale(1.7)', opacity: 0 },
                '100%': { transform: 'scale(1.7)', opacity: 0 },
              },
              animation: 'missionThreatRing 2.4s ease-out infinite',
            }}
          />
          <Box
            aria-hidden
            sx={{
              width: 12,
              height: 12,
              transform: 'rotate(45deg)',
              bgcolor: pin.color,
              border: '1.5px solid #ffffff',
              boxShadow: `0 0 5px ${pin.color}`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 26,
              px: 0.6,
              borderRadius: 0.75,
              bgcolor: 'rgba(11,16,23,0.82)',
              border: '1px solid rgba(255,255,255,0.14)',
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              fontSize: '0.58rem',
              fontWeight: 700,
              lineHeight: 1.6,
              color: '#e8eef7',
              ...pinLabelSx(labelSide),
            }}
          >
            {pin.label}
          </Box>
        </Box>
      </RichTooltip>
    </Marker>
  );
};

export default MissionThreatLayer;
